/**
 * Warms the lazy chunk for an event sub-route on sidebar hover so the
 * click-then-navigate sequence skips the chunk-download wait.
 *
 * v1 only handles the chunk. Per-page query prefetching is intentionally
 * deferred: it requires factoring each page's queryKey + queryFn into a
 * shared module that both the page and this prefetcher can import.
 * Without that, a duplicated queryKey here would drift from the page and
 * the prefetch would silently fill a different cache slot.
 *
 * Specifiers below MUST match App.tsx's `lazy(() => import(...))` strings
 * byte-for-byte — Vite/Rollup keys chunk identity off the specifier.
 */

const REGISTRY: Record<string, () => Promise<unknown>> = {
  dashboard: () => import("@/pages/event/EventDashboardPage"),
  goals: () => import("@/pages/event/EventGoalsPage"),
  website: () => import("@/pages/event/EventWebsitePage"),
  "guests/guestlist": () =>
    import("@/pages/event/guests/EventGuestsGuestlistPage"),
  "guests/requests": () =>
    import("@/pages/event/guests/EventGuestsRequestsPage"),
  "guests/trash": () => import("@/pages/event/guests/EventGuestsTrashPage"),
  "check-in/history": () => import("@/pages/event/EventCheckInHistoryPage"),
  "check-in/devices": () => import("@/pages/event/EventCheckInDevicesPage"),
  seating: () => import("@/pages/event/EventSeatingPage"),
  attachments: () => import("@/pages/event/EventAttachmentsPage"),
  documents: () => import("@/pages/event/EventDocumentsPage"),
  programme: () => import("@/pages/event/EventContentProgrammePage"),
  speakers: () => import("@/pages/event/EventContentSpeakersPage"),
  sponsors: () => import("@/pages/event/EventContentSponsorsPage"),
  "settings/media": () =>
    import("@/pages/event/settings/EventSettingsMediaPage"),
  "automation/workflows": () =>
    import("@/pages/event/automation/EventAutomationWorkflowsPage"),
  "automation/history": () =>
    import("@/pages/event/automation/EventAutomationHistoryPage"),
  "messaging/mail-templates": () =>
    import("@/pages/event/messaging/EventMessagingMailTemplatesPage"),
  "messaging/sent-mails": () =>
    import("@/pages/event/messaging/EventMessagingSentMailsPage"),
  "messaging/sender": () =>
    import("@/pages/event/messaging/EventMessagingSenderPage"),
  "sms/sent-sms": () => import("@/pages/event/sms/EventSmsSentPage"),
  "sms/team-numbers": () => import("@/pages/event/sms/EventSmsTeamNumbersPage"),
  "sms/templates": () => import("@/pages/event/sms/EventSmsTemplatesPage"),
  "settings/general": () =>
    import("@/pages/event/settings/EventSettingsGeneralPage"),
  "settings/rules": () =>
    import("@/pages/event/settings/EventSettingsRulesPage"),
  "settings/plugins": () =>
    import("@/pages/event/settings/EventSettingsPluginsPage"),
  "settings/notifications": () =>
    import("@/pages/event/settings/EventSettingsNotificationsPage"),
  // The access entry renders three lazy routes nested in each other: the
  // layout (role context), the tabs, and the Members tab inside them.
  "settings/access": () =>
    Promise.all([
      import("@/pages/event/settings/access/EventAccessLayout"),
      import("@/pages/event/settings/access/EventAccessTabs"),
      import("@/pages/event/settings/access/EventMembersTab"),
    ]),
};

export function prefetchEventRoute(url: string): void {
  const match = url.match(/^\/event\/[^/]+\/(.+)$/);
  if (!match) return;
  const loader = REGISTRY[match[1]];
  if (!loader) return;
  loader().catch(() => {
    // A failed prefetch is non-fatal: the on-click navigation will retry
    // and surface any real error through the route-level Suspense + the
    // stale-chunk reload listener installed in main.tsx.
  });
}

// Top-N likely-next destinations. Used by EventLayoutShell for an idle-time
// warm so users on touch devices / keyboard nav (no hover) still skip the
// chunk-download wait on first sub-route click. Browser dedupes already-
// loaded modules, so it's safe to call even when the user is on one of
// these routes.
const COMMON_ROUTES = [
  "dashboard",
  "guests/guestlist",
  "check-in/history",
  "messaging/mail-templates",
  "settings/general",
];

export function warmCommonEventRoutes(): void {
  for (const key of COMMON_ROUTES) {
    REGISTRY[key]?.().catch(() => {});
  }
}
